import React from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { DASH, dashPageSx } from "../../shared/dashboardEnterprise";

export const EXD = {
  bg: DASH.bg,
  surface: DASH.surface,
  surface2: DASH.surface2,
  border: DASH.border,
  text: DASH.text,
  muted: DASH.muted,
  faint: DASH.faint,
  navy: DASH.navy,
  accent: DASH.accent,
  good: "#16a36a",
  bad: "#d6334a",
  warn: "#d98a06",
  font: DASH.font,
  scrollThumb: DASH.scrollThumb,
  scrollThumbHover: DASH.scrollThumbHover,
  scrollTrack: DASH.scrollTrack,
};

/** Thin themed scrollbar for MUI `sx` on overflow containers */
export { dashScrollbarSx as scrollbarSx } from "../../shared/dashboardEnterprise";

export const pageSx = {
  ...dashPageSx,
};

export const gridSx = (columns) => ({
  display: "grid",
  gap: "12px",
  width: "100%",
  gridTemplateColumns: columns,
});

export const GRID = {
  kpi: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(3, minmax(0, 1fr))",
    lg: "repeat(6, minmax(0, 1fr))",
  },
  ops: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  sales: {
    xs: "minmax(0, 1fr)",
    md: "minmax(0, 2.2fr) minmax(0, 1fr)",
  },
  three: {
    xs: "minmax(0, 1fr)",
    md: "repeat(3, minmax(0, 1fr))",
  },
  fin: {
    xs: "minmax(0, 1fr)",
    md: "minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr)",
  },
};

export const widgetSx = (variant = "default") => ({
  bgcolor: EXD.surface,
  border: `1px solid ${EXD.border}`,
  borderRadius: "10px",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  gap: 1,
  height: "100%",
  minHeight:
    variant === "chart" ? 280 : variant === "tall" ? 340 : 0,
  minWidth: 0,
  overflow: "visible",
  p: "12px 14px",
  position: "relative",
  width: "100%",
  zIndex: 1,
  "&:hover, &:focus-within": { zIndex: 80 },
});

export const chartWrapSx = (minHeight = 220) => ({
  flex: "1 1 auto",
  minHeight,
  overflow: "visible",
  position: "relative",
  width: "100%",
  zIndex: 1,
  "& .highcharts-container, & .highcharts-root": {
    overflow: "visible !important",
  },
});

export const ExdCrumb = ({ children }) => (
  <Box
    component="span"
    sx={{ color: EXD.muted, fontSize: 12.5, fontWeight: 500, whiteSpace: "nowrap" }}
  >
    {children}
  </Box>
);

export const ExdHint = ({ children }) => (
  <Typography
    sx={{
      alignItems: "center",
      color: EXD.faint,
      display: "flex",
      fontSize: 11.5,
      gap: 0.75,
      m: 0,
    }}
  >
    {children}
  </Typography>
);

export const ExdEmpty = ({ children = "No data" }) => (
  <Typography sx={{ color: EXD.faint, fontSize: 13, py: 2, textAlign: "center" }}>
    {children}
  </Typography>
);

export const ExdStatLine = ({ children }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    useFlexGap
    spacing={2.25}
    sx={{
      color: EXD.muted,
      fontSize: 12.5,
      "& b, & strong": {
        color: EXD.text,
        display: "block",
        fontSize: 15,
        fontWeight: 700,
      },
    }}
  >
    {children}
  </Stack>
);

export const ExdStatItem = ({ label, children, compact = false }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: compact ? 0 : 0.25,
      minWidth: 0,
    }}
  >
    <Typography
      component="span"
      noWrap={compact}
      sx={{
        color: EXD.muted,
        fontSize: compact ? 10.5 : 12.5,
        fontWeight: 500,
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    <Box
      component="b"
      sx={{
        color: EXD.text,
        fontSize: compact ? 13 : 15,
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: compact ? "nowrap" : "normal",
      }}
    >
      {children}
    </Box>
  </Box>
);

export const ExdLegendRow = ({ color, label, value, round }) => (
  <Stack direction="row" alignItems="center" spacing={0.875} sx={{ minWidth: 0 }}>
    <Box
      sx={{
        bgcolor: color,
        borderRadius: round ? "50%" : "3px",
        flex: "none",
        height: 9,
        width: 9,
      }}
    />
    <Typography
      noWrap
      title={typeof label === "string" ? label : undefined}
      sx={{ color: EXD.text, flex: 1, fontSize: 12.5, fontWeight: 600, minWidth: 0 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: EXD.text,
        flex: "none",
        fontFamily: EXD.font,
        fontSize: 12.5,
        fontVariantNumeric: "tabular-nums",
        fontWeight: 600,
        textAlign: "right",
      }}
    >
      {value}
    </Typography>
  </Stack>
);

export const ExdRankRow = ({ label, value, barPct, color, extra, ...rest }) => (
  <Box
    sx={{
      display: "grid",
      gap: "4px 8px",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      width: "100%",
      cursor: "default",
    }}
    {...rest}
  >
    <Typography
      noWrap
      title={typeof label === "string" ? label : undefined}
      sx={{ color: EXD.text, fontSize: 12.5, fontWeight: 600, minWidth: 0 }}
    >
      {label}
      {extra ? (
        <Box component="small" sx={{ color: EXD.faint, fontWeight: 500 }}>
          {" "}
          {extra}
        </Box>
      ) : null}
    </Typography>
    <Typography
      sx={{
        color: EXD.text,
        fontFamily: EXD.font,
        fontSize: 12.5,
        fontVariantNumeric: "tabular-nums",
        fontWeight: 600,
      }}
    >
      {value}
    </Typography>
    <Box
      sx={{
        bgcolor: EXD.surface2,
        borderRadius: 99,
        gridColumn: "1 / -1",
        height: 7,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: color,
          borderRadius: 99,
          display: "block",
          height: "100%",
          minWidth: 4,
          width: `${Math.max(barPct, 2)}%`,
        }}
      />
    </Box>
  </Box>
);

export const ExdListRow = ({ rank, name, sub, trailing, meter, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    onClick={onClick}
    sx={{
      borderBottom: `1px dashed ${EXD.border}`,
      cursor: onClick ? "pointer" : "default",
      py: 1,
      "&:last-child": { borderBottom: 0 },
    }}
  >
    <Typography
      sx={{
        color: EXD.faint,
        flex: "none",
        fontFamily: EXD.font,
        fontSize: 11,
        fontVariantNumeric: "tabular-nums",
        fontWeight: 700,
        width: 22,
      }}
    >
      {rank}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        noWrap
        sx={{ color: EXD.text, fontSize: 13, fontWeight: 600 }}
      >
        {name}
      </Typography>
      {sub ? (
        <Typography noWrap sx={{ color: EXD.muted, fontSize: 11.5 }}>
          {sub}
        </Typography>
      ) : null}
      {meter || null}
    </Box>
    {trailing}
  </Stack>
);

export const ExdMiniMeter = ({ pct, tone = "neutral" }) => {
  const bar =
    tone === "bad" ? EXD.bad : tone === "warn" ? EXD.warn : EXD.accent;
  return (
    <Box
      sx={{
        bgcolor: EXD.surface2,
        borderRadius: 99,
        height: 5,
        mt: 0.5,
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          bgcolor: bar,
          borderRadius: 99,
          height: "100%",
          width: `${Math.max(pct, 2)}%`,
        }}
      />
    </Box>
  );
};

export const ExdChip = ({ tone = "neutral", children }) => {
  const map = {
    good: { bgcolor: "#e2f6ec", color: EXD.good },
    warn: { bgcolor: "#fdf2dc", color: EXD.warn },
    bad: { bgcolor: "#fde6ea", color: EXD.bad },
    neutral: { bgcolor: EXD.surface2, color: EXD.text },
  };
  const t = map[tone] || map.neutral;
  return (
    <Chip
      size="small"
      label={children}
      sx={{
        bgcolor: t.bgcolor,
        color: t.color,
        fontSize: 11.5,
        fontWeight: 700,
        height: 24,
        borderRadius: "7px",
      }}
    />
  );
};

export const ExdWidgetSkeleton = () => (
  <Paper elevation={0} sx={widgetSx()}>
    <Stack spacing={1.25}>
      <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 18, width: "55%" }} />
      <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 12, width: "100%" }} />
      <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 72, width: "100%" }} />
      <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 16, width: "70%" }} />
    </Stack>
  </Paper>
);
